import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus, Settings, FileText, ChevronLeft, Building2, FolderOpen } from 'lucide-react'
import { useState } from 'react'

import { KnowledgeBaseSettingsDialog } from '@/components/knowledge-base/knowledge-base-settings-dialog'
import { DocumentTree } from '@/components/knowledge-base/document-tree'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useKnowledgeBases,
  useDocuments,
} from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/knowledge-base/spaces/$spaceId')({
  component: KnowledgeBasePage,
})

/**
 * 知识库页面
 * 显示知识库内的文档列表
 */
function KnowledgeBasePage() {
  const params = Route.useParams()
  const knowledgeBaseId = params.spaceId
  const navigate = useNavigate()
  const { data: knowledgeBases, isLoading: kbLoading } = useKnowledgeBases()
  const { data: documents, isLoading: docsLoading } = useDocuments(knowledgeBaseId)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | undefined>()
  const [showSettings, setShowSettings] = useState(false)

  // 从列表中找到当前知识库
  const knowledgeBase = knowledgeBases?.find((kb) => kb.id === knowledgeBaseId)

  const handleBack = () => {
    void navigate({ to: '/knowledge-base' })
  }

  const handleCreateDocument = () => {
    void navigate({
      to: '/knowledge-base/documents/$documentId',
      params: { documentId: 'new' },
      search: { knowledgeBaseId },
    })
  }

  const handleSelectDocument = (documentId: string) => {
    setSelectedDocumentId(documentId)
    void navigate({ to: '/knowledge-base/documents/$documentId', params: { documentId } })
  }

  const handleKBSettings = () => {
    setShowSettings(true)
  }

  if (kbLoading) {
    return <KnowledgeBasePageSkeleton />
  }

  if (!knowledgeBase) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-medium">知识库不存在</h2>
          <p className="text-sm text-muted-foreground mt-1">该知识库可能已被删除或您没有访问权限</p>
          <Button variant="outline" className="mt-4" onClick={handleBack}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            返回知识库
          </Button>
        </div>
      </div>
    )
  }

  const canEdit = knowledgeBase.permission === 'admin' || knowledgeBase.permission === 'editor'
  const isAdmin = knowledgeBase.permission === 'admin'

  return (
    <div className="flex h-full flex-col">
      <KnowledgeBaseSettingsDialog
        knowledgeBase={knowledgeBase}
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <button onClick={handleBack} className="hover:text-foreground">
            知识库
          </button>
          <span>/</span>
          <span className="text-foreground">{knowledgeBase.name}</span>
        </div>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-muted text-3xl">
              {knowledgeBase.type === 'company' ? (
                <Building2 className="h-7 w-7 text-primary" />
              ) : (
                <FolderOpen className="h-7 w-7 text-muted-foreground" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{knowledgeBase.name}</h1>
              {knowledgeBase.description && (
                <p className="text-muted-foreground mt-1">{knowledgeBase.description}</p>
              )}
              <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                <span
                  className={cn(
                    'px-2 py-0.5 rounded text-xs',
                    knowledgeBase.type === 'company'
                      ? 'bg-blue-500/10 text-blue-600'
                      : 'bg-green-500/10 text-green-600'
                  )}
                >
                  {knowledgeBase.type === 'company' ? '公司知识库' : '项目知识库'}
                </span>
                {knowledgeBase.project && (
                  <span>项目: {knowledgeBase.project.name}</span>
                )}
                <span className="inline-flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  {documents?.total ?? 0} 文档
                </span>
                {knowledgeBase.permission && (
                  <span>{getPermissionLabel(knowledgeBase.permission)}</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isAdmin && knowledgeBase.type === 'company' && (
              <Button variant="outline" size="sm" onClick={handleKBSettings}>
                <Settings className="mr-2 h-4 w-4" />
                管理编辑者
              </Button>
            )}
            {canEdit && (
              <Button size="sm" onClick={handleCreateDocument}>
                <Plus className="mr-2 h-4 w-4" />
                新建文档
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Document Tree Sidebar */}
        <aside className="w-64 border-r overflow-auto">
          <div className="p-4">
            {docsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-8" />
                ))}
              </div>
            ) : (
              <DocumentTree
                knowledgeBaseId={knowledgeBaseId}
                selectedDocumentId={selectedDocumentId}
                onSelectDocument={handleSelectDocument}
                onCreateDocument={canEdit ? handleCreateDocument : undefined}
              />
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <h3 className="font-medium text-foreground">选择一个文档</h3>
            <p className="text-sm mt-1">从左侧选择文档查看内容</p>
            {canEdit && (
              <Button className="mt-4" onClick={handleCreateDocument}>
                <Plus className="mr-2 h-4 w-4" />
                创建新文档
              </Button>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

function KnowledgeBasePageSkeleton() {
  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-6 py-4">
        <Skeleton className="h-4 w-32 mb-2" />
        <div className="flex items-center gap-4">
          <Skeleton className="h-14 w-14 rounded-lg" />
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
            <Skeleton className="h-4 w-32 mt-2" />
          </div>
        </div>
      </div>
      <div className="flex-1 flex">
        <div className="w-64 border-r p-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-8 mb-2" />
          ))}
        </div>
        <div className="flex-1" />
      </div>
    </div>
  )
}

function getPermissionLabel(permission: string): string {
  switch (permission) {
    case 'admin':
      return '管理员'
    case 'editor':
      return '编辑者'
    case 'viewer':
      return '查看者'
    default:
      return ''
  }
}
